import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { APP_CURRENCY_NAME, ROUTES } from 'config/constants';
import ProductActions from '../ProductActions/ProductActions';
import AccountLayout from 'layouts/AccountLayout/AccountLayout';

import NoImage from 'assets/images/noImg.png';
import backArrow from 'assets/images/general/back-arrow.svg';

import style from './AfterBuy.module.scss';
import { calculateGST, getSelectedStorageLabel } from 'utils/helpers';

AfterBuy.propTypes = {
    product: PropTypes.object.isRequired,
    handleRiseTicket: PropTypes.func,
    submitLoading: PropTypes.bool,
    rentalOptions: PropTypes.array,
    selectedRentalPeriod: PropTypes.object,
    setSelectedRentalPeriod: PropTypes.func,
    changeRentalTerms: PropTypes.func,
    handleBuyNow: PropTypes.func,
    handleReturnOrder: PropTypes.func,
    order: PropTypes.any,
    isOpenFirst: PropTypes.bool,
    setIsOpenFirst: PropTypes.func,
    isOpenSecond: PropTypes.bool,
    setIsOpenSecond: PropTypes.func,
    componnetType: PropTypes.any,
    setComponnetType: PropTypes.func,
    riseTicket: PropTypes.object,
    setRiseTicket: PropTypes.func,
    returnOrderType: PropTypes.string,
    setReturnOrderType: PropTypes.func,
    isProductDA: PropTypes.bool
};

function AfterBuy({
    product,
    handleRiseTicket,
    submitLoading,
    rentalOptions,
    selectedRentalPeriod,
    setSelectedRentalPeriod,
    changeRentalTerms,
    handleBuyNow,
    handleReturnOrder,
    order,
    isOpenFirst,
    setIsOpenFirst,
    isOpenSecond,
    setIsOpenSecond,
    componnetType,
    setComponnetType,
    riseTicket,
    setRiseTicket,
    returnOrderType,
    setReturnOrderType,
    isProductDA = false
}) {
    const history = useHistory();
    const [latestInvoice] = useState(order?.invoice_payments?.reverse()[0] ?? null);
    const [firstTransaction] = useState(order?.transactions[0] ?? null);
    const [isExtraMonthPromo, setIsExtraMonthPromo] = useState(false);

    const convertToDateString = date => {
        if (!date) {
            return '-';
        }

        const d = new Date(date);
        const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(d);
        const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(d);
        const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(d);

        return `${da} ${mo} ${ye}`;
    };

    const getRentalPeriod = () => {
        const durationTime = product?.rental_period.find(e => e.id === order?.rental_period_id)?.duration_time;

        return isExtraMonthPromo ? (durationTime + 1) : durationTime;
    };

    const durationLeftInMonths = () => {
        const currentDate = new Date();
        const deliveredDate = new Date(order?.delivered_at);
        const subscriptionEndsDate = new Date(order?.ends_at);
        const subscriptionLeft = Math.abs(currentDate.getTime() - deliveredDate.getTime());
        const actualSubscriptionPeriod = getRentalPeriod();
        const subscriptionLeftInMonths = subscriptionEndsDate > currentDate ? Math.ceil(subscriptionLeft / (2e3 * 3600 * 365.25)) : actualSubscriptionPeriod;
        const diffInMonthsWithSubscriptionLeft = Number(getRentalPeriod()) - Number(subscriptionLeftInMonths);

        return diffInMonthsWithSubscriptionLeft < 0 ? 0 : diffInMonthsWithSubscriptionLeft;
    };

    const handleDate = t => {
        if (t !== null) {
            return moment(t).format('DD/MM/YYYY');
        }

        return '-';
    };

    const getPDFlink = d => {
        const txs = order.transactions.filter((t) => {
            return moment(d).format('DD/MM/YYYY') === moment(t.created_at).format('DD/MM/YYYY');
        });

        return txs.length > 0 ? txs[0].invoice_pdf_link : null;
    };

    const handlePDF = t => {
        const pdfLink = getPDFlink(t.paid_at);

        if (pdfLink) {
            window.open(ROUTES.INVOICE(btoa(pdfLink)), '_blank');
        }

        return true;
    };

    const formatNumber = (number) => {
        const num = Number(number);

        if (num % 1 === 0) {
            return num.toLocaleString('en-US');
        } else {
            return num.toFixed(2).toLocaleString('en-US');
        }
    };

    const isAfterGstStartDate = (paidAt) => {
        const paidDate = new Date(paidAt);

        // 2025 March 1st 00:00:00
        const marchFirst = new Date(2025, 2, 1, 0, 0, 0, 0);

        return paidDate >= marchFirst;
    };

    useEffect(() => {
        if (order.coupon) {
            setIsExtraMonthPromo((order.subtotal - order.total) === 0);
        }
    }, [order]);

    return (<AccountLayout>
        <div className={style.section}>
            <div className={style.sectionWrapper}>
                {order && <img onClick={() => history.goBack()} src={backArrow} className={style.backArrow}
                    alt='back'/>}
                <div className={style.productDetails}>
                    <img className={style.productImage} src={order.image_url ?? NoImage} alt={product.name}/>
                    <div className={style.productInfo}>
                        <p className={style.title}>{product?.name}</p>
                        <p className={style.attribute}>
                            {product?.attributes?.storage?.values && <span>{getSelectedStorageLabel(product.attributes.storage.values.find(e => e.id === order.product_attribute_value_id)?.value, isProductDA)}&nbsp;|&nbsp;</span>}
                            <span>{product?.colors.find(e => e.id === order.color_id)?.name}</span>
                        </p>
                        <p className={style.orderId}>Order ID: {order?.order_number ?? '-'}</p>
                    </div>
                </div>
                <div className={style.subscriptionInfo}>
                    <div>
                        <p className={style.content}>Subscription Amount:</p>
                        <p className={`${style.content} ${style.font16} ${style.fontSemibold}`}>{APP_CURRENCY_NAME }&nbsp;{order?.subtotal?.toLocaleString('en-US')}&nbsp;/&nbsp;mo</p>
                    </div>
                    {durationLeftInMonths() > 0 && <div>
                        <p className={style.content}>Incl. GST (9%)</p>
                        <p className={`${style.content} ${style.font14}`}>SGD &nbsp;{calculateGST(order?.subtotal)}&nbsp;/&nbsp;mo</p>
                    </div>}
                    {isExtraMonthPromo ? (
                        <>
                            <div>
                                <p className={`${style.content} ${style.font12}`}>Promo Benefit ({order?.coupon}):</p>
                                <p className={`${style.content} ${style.font14} ${style.fontSemibold}`}>1 month free</p>
                            </div>
                        </>
                    ) : (
                        order?.coupon && (
                            <>
                                <div>
                                    <p className={`${style.content} ${style.font12}`}>Discount ({order?.coupon}):</p>
                                    <p className={`${style.content} ${style.font14} ${style.fontSemibold}`}>- {APP_CURRENCY_NAME }&nbsp;{formatNumber(Number(order?.subtotal) - Number(order?.total))}&nbsp;/&nbsp;mo</p>
                                </div>
                                <div>
                                    <p className={style.content}>Total Payable :</p>
                                    <p className={`${style.content} ${style.font16} ${style.fontSemibold}`}>{APP_CURRENCY_NAME }&nbsp;{formatNumber(order?.total)}&nbsp;/&nbsp;mo</p>
                                </div>
                            </>
                        )
                    )}
                    <div>
                        <p className={style.content}>Subscribed for</p>
                        <p className={style.content}>{getRentalPeriod()} {getRentalPeriod() > 1 ? ' months' : ' month'}</p>
                    </div>
                    {order?.status === 'subscribed' && <div>
                        <p className={style.content}>Subscribed on</p>
                        <p className={style.content}>{convertToDateString(order?.starts_at)}</p>
                    </div>}
                    <div>
                        <p className={style.content}>Valid till</p>
                        <div className={style.duration}>
                            {durationLeftInMonths() > 0
                                ? <p className={style.remainingDuration}>{durationLeftInMonths()} months left</p>
                                : <p className={style.over}>{order?.starts_at ? 'Complete' : order?.status}</p>
                            }
                            {order?.status === 'subscribed' && <p className={style.content}>{convertToDateString(order?.ends_at)}</p>}
                        </div>
                    </div>
                </div>

                <div className={style.productActions}>
                    {/* {order?.status === 'subscribed' && <div className={style.productActions}> */}
                    <ProductActions
                        product={product}
                        returnOrderType={returnOrderType}
                        setReturnOrderType={setReturnOrderType}
                        riseTicket={riseTicket}
                        setRiseTicket={setRiseTicket}
                        isOpenFirst={isOpenFirst}
                        setIsOpenFirst={setIsOpenFirst}
                        isOpenSecond={isOpenSecond}
                        setIsOpenSecond={setIsOpenSecond}
                        componnetType={componnetType}
                        setComponnetType={setComponnetType}
                        order={order}
                        handleReturnOrder={handleReturnOrder}
                        price={String(APP_CURRENCY_NAME + ' ' + product.purchase_price)}
                        handleBuyNow={handleBuyNow}
                        selectedRentalPeriod={selectedRentalPeriod}
                        setSelectedRentalPeriod={setSelectedRentalPeriod}
                        changeRentalTerms={changeRentalTerms}
                        handleRiseTicket={handleRiseTicket}
                        submitLoading={submitLoading}
                        rentalOptions={rentalOptions}/>
                </div>

                {latestInvoice !== null && <div className={style.billingDetailsContainer}>
                    <p className={style.title}>Billing Details</p>
                    <div className={style.billingDetails} key={latestInvoice?.id}>
                        <div className={style.dateContainer}>
                            <p className={`${style.title} ${style.billing_date}`}>Date: <span className={style.date}>{handleDate(latestInvoice?.period_start)}&nbsp;-&nbsp;{handleDate(latestInvoice?.period_end)}</span></p>
                            {latestInvoice.event === 'invoice.paid' && <div className={style.paid}>Paid</div> }
                            {latestInvoice.event === 'invoice.payment_failed' && <div className={style.pending}>Pending</div> }
                        </div>
                        <div className={style.amountGstContainer}>
                            <p className={style.title}>Amount Paid : {APP_CURRENCY_NAME}&nbsp;{latestInvoice?.amount_paid?.toLocaleString('en-US')}</p>
                            {isAfterGstStartDate(latestInvoice.paid_at) && <p className={style.title}>{'(Incl. GST 9%)'}</p>}
                        </div>
                        {latestInvoice.event !== 'invoice.payment_failed' && <p onClick={() => { handlePDF(latestInvoice); }} className={style.downloadPdf}>
                            View Invoice
                        </p>}
                        {latestInvoice.event === 'invoice.payment_failed' && (
                            <div className={style.completePaymentContainer}>
                                <a href={latestInvoice?.invoice_url} target='_blank'
                                    rel='noopener noreferrer' className={style.completePayment}>Complete payment</a>
                                <p className={style.errorNote}>*Your payment is pending, Complete your pending payment to avoid restrictions on your device</p>
                                <p className={style.remainingTime}>21 hrs left</p>
                            </div>
                        )}
                    </div>
                    {order?.invoice_payments?.length > 0 && <p className={`${style.lastPayments} d-none`}>View Past Payments</p>}
                </div>}

                <div className={style.shippingAddress}>
                    <p className={style.title}>Shipping Address</p>
                    <p className={style.content}>
                        <span>{order?.address?.full_name}</span><br/>
                        {order?.address?.block && <span>Block: { order?.address?.block},</span>}
                        {order?.address?.street && <><span> Street: { order?.address?.street}</span><br/></>}
                        {order?.address?.unit && <span>Unit# { order?.address?.unit},</span>}
                        {order?.address?.building && <span> Building: { order?.address?.building},</span>}
                        {order?.address?.floor && <><span> Floor: { order?.address?.floor}</span><br/></>}
                        {order?.address?.country && <span>{ order?.address?.country }</span> } {order?.address?.postalCode && <span>{ order?.address?.postalCode }</span>}
                        <div><span>{ order?.address?.mobile }</span></div>
                    </p>
                </div>

                {firstTransaction && <div className={style.paymentMode}>
                    <p className={style.title}>Mode of Payment</p>
                    <div className={style.card}>
                        <div className={style.cardPlaceholder} />
                        <span className={style.last4}>**** **** **** {firstTransaction?.last4}</span>
                    </div>
                </div>}
            </div>
        </div>
    </AccountLayout>);
}

export default AfterBuy;
